import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from "../../context/AuthContext/AuthContext";

import AppHeader from '../../components/AppHeader/AppHeader';
import AppSidebar from '../../components/AppSidebar/AppSidebar';
import '../../components/AppShell/AppShell.css';
import DashboardSideNav from '../../components/Dashboard/DashboardSideNav';
import { useLocation } from 'react-router-dom';

const PageContext = createContext();

const HIDE_NAV_PREFIXES = [
    '/dashboard/listings/new',
    '/dashboard/listings/edit',
    '/dashboard/listings/bulk',
    '/register',
    '/host/register',
    '/carrier/register',
    '/driver/register',
    '/in-review',
];

const HOST_ROLES = new Set(['HOST_ADMIN', 'PROPERTY_MANAGER']);

export function pickChrome(pathname, role, isAuthLoading) {
    if (HIDE_NAV_PREFIXES.some((p) => pathname.startsWith(p))) {
        return 'wizard';
    }
    if (pathname === '/find-parking') {
        return 'app';
    }
    if (isAuthLoading || !role) {
        return 'legacy';
    }
    if (
        pathname === '/'
        || pathname.startsWith('/dashboard')
        || pathname.startsWith('/profile')
        || pathname.startsWith('/company')
    ) {
        return 'app';
    }
    return 'site';
}

export const PageProvider = ({ children }) => {
    const { profile, isAuthLoading } = useAuth();
    const location = useLocation();
    const role = profile?.roles?.primary;

    const [showTopNavigation, setShowTopNavigation] = useState(true);
    const [showDesktopSideNav, setShowDesktopSideNav] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    const [pageTitle, setPageTitle] = useState(document.title);
    const defaultTitle = useRef(document.title);

    const pageTitleSuffix = ' - Park Pro';
    useEffect(() => {
        document.title = pageTitle ? `${pageTitle}${pageTitleSuffix}` : 'Park Pro';
        return () => {
            document.title = defaultTitle.current || 'Park Pro';
        };
    }, [pageTitle]);

    useEffect(() => {
        const isHideNavPath = HIDE_NAV_PREFIXES.some(
            p => location.pathname.startsWith(p),
        );
        if (!isHideNavPath) {
            setShowTopNavigation(true);
            setShowDesktopSideNav(true);
        }
        setMobileSidebarOpen(false);
        setPageTitle(null);
    }, [location.pathname]);

    const chrome = pickChrome(location.pathname, role, isAuthLoading);
    const isHideNavPath = chrome === 'wizard';
    const showAppSidebar = chrome === 'app' && location.pathname !== '/find-parking';
    const showHeader = showTopNavigation && !isHideNavPath;
    const showSiteSidebar = !isHideNavPath && chrome !== 'app';

    const contextValue = {
        setPageTitle,
        showTopNavigation,
        setShowTopNavigation,
        showDesktopSideNav,
        setShowDesktopSideNav,
        mobileSidebarOpen,
        setMobileSidebarOpen,
    };

    if (chrome === 'app') {
        return (
            <PageContext.Provider value={contextValue}>
                <div className="flex flex-col h-full font-['Oxanium'] app-shell-root">
                    {showHeader && (
                        <AppHeader role={role} variant="app" showMobileMenu={Boolean(role)} />
                    )}
                    <div className="app-shell">
                        {showAppSidebar && <AppSidebar roles={profile?.roles} />}
                        <main className="app-main">
                            {children}
                        </main>
                    </div>
                </div>
            </PageContext.Provider>
        );
    }

    return (
        <PageContext.Provider value={contextValue}>
            <div className={`flex flex-col h-full font-['Oxanium'] app-shell-root ${isHideNavPath ? 'min-h-0' : ''}`}>
                {showHeader && (
                    <AppHeader
                        role={role}
                        variant="site"
                        showHostNavLinks={HOST_ROLES.has(role)}
                        // Signed-in users get the menu button on every page in this frame
                        // (it opens MobileNav, including on property and booking pages);
                        // signed-out visitors keep today's rule for the older slide-in menu.
                        showMobileMenu={Boolean(role) || (showSiteSidebar && showDesktopSideNav)}
                    />
                )}
                <div className={`flex flex-row flex-1 min-h-0 ${isHideNavPath ? 'overflow-hidden' : ''}`}>
                    {showSiteSidebar && (
                        <DashboardSideNav roles={profile?.roles?.all} />
                    )}
                    <main className={`app-main ${isHideNavPath ? 'min-h-0 overflow-hidden' : ''}`}>
                        {children}
                    </main>
                </div>
            </div>
        </PageContext.Provider>
    );
};

export const usePageContext = () => useContext(PageContext);
